import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Copy, MapPin, Package, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatEUR, getBrowserTz } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import StatusTimeline from "../components/StatusTimeline";
import WhatsAppContextualLink from "../components/WhatsAppContextualLink";
import WhatsAppActionButton from "../components/WhatsAppActionButton";
import ShipmentCelebration from "../components/ShipmentCelebration";
import DeliveryEvidenceCard from "../components/DeliveryEvidenceCard";
import {
  SHIPMENT_STATUS_LABELS,
  SHIPMENT_STATUS_BADGE_CLASSES,
} from "../lib/shipment-statuses";

const TYPE_LABELS = {
  ropa: "Ropa",
  alimentos: "Alimentos",
  electronica: "Electrónica",
  tv: "TV",
  medicinas: "Medicinas",
  herramientas: "Herramientas",
  otros: "Otros",
};

const ELECTRONICS_SUBTYPE_LABELS = {
  nevera: "Nevera",
  lavadora: "Lavadora",
  microondas: "Microondas",
  // Kept for historical shipments — the option is no longer offered in the wizard.
  vitroceramica: "Vitrocerámica",
  otros: "Otros aparatos",
};

// Phase 46b — wizard-v2 subcategory labels. Preferred over the legacy
// ELECTRONICS_SUBTYPE_LABELS when a shipment carries `shipment_subcategory`.
const SUBCATEGORY_LABELS = {
  general: "General",
  bicis: "Bicis",
  bicicleta: "Bicis",
  microondas: "Microondas",
  neveras: "Neveras",
  nevera: "Neveras",
  patinetes: "Patinetes",
  patinete: "Patinetes",
  silla_ruedas: "Silla de ruedas",
  otro: "Otros",
};

const STATUS_LABELS = SHIPMENT_STATUS_LABELS;
const STATUS_CLASSES = SHIPMENT_STATUS_BADGE_CLASSES;

// Phase 47 — user-visible timeline. Shows the granular transit steps
// so senders can see exactly where the package is on any given day.
const SHIPMENT_STEPS = [
  { code: "confirmado_pendiente_recogida", label: "Confirmado" },
  { code: "recogido", label: "Recogido" },
  { code: "en_puerto_almeria", label: "Puerto Almería" },
  { code: "en_puerto_oran", label: "Puerto Orán" },
  { code: "en_aduanas", label: "Aduanas" },
  { code: "camino_campamentos", label: "En camino" },
  { code: "reparto", label: "En reparto" },
  { code: "entregado", label: "Entregado" },
];

export default function EnvioConfirmacionPage() {
  const { id } = useParams();
  const [shipment, setShipment] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { user } = useAuth();

  const load = useCallback(() => {
    api
      .get(`/shipments/${id}`)
      .then((r) => setShipment(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="text-3xl font-black tracking-tight">Envío no encontrado</h1>
        <p className="mt-3 text-neutral-500">
          Es posible que el enlace no sea válido o que estés en otro dispositivo.
        </p>
        <Link
          to="/"
          className="mt-8 inline-block rounded-full bg-[#00953B] hover:bg-[#007a30] text-white px-6 py-3 font-semibold"
        >
          Volver al inicio
        </Link>
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="h-64 rounded-2xl bg-neutral-100 animate-pulse" />
      </div>
    );
  }

  const shortId = shipment.id.slice(0, 8);
  const status = shipment.status || "pendiente";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const downloadPdf = async () => {
    try {
      const resp = await api.get(
        `/shipments/${shipment.id}/receipt.pdf?tz=${encodeURIComponent(getBrowserTz())}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `MercaGo-envio-${shortId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el recibo");
    }
  };

  return (
    <div
      data-testid="envio-confirmacion-page"
      className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-10"
    >
      <ShipmentCelebration shipmentCreatedAt={shipment.created_at} />
      <div className="rounded-3xl bg-gradient-to-br from-[#00953B] to-[#00753a] text-white px-6 sm:px-10 py-10">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-white/15 grid place-items-center">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-white/80">
              Envío confirmado
            </div>
            <h1 className="text-2xl sm:text-3xl font-black leading-tight">
              Envío{" "}
              <span data-testid="envio-id-short" className="font-mono">
                #{shortId}
              </span>{" "}
              registrado
            </h1>
          </div>
        </div>
        <p className="mt-4 text-white/90 max-w-lg text-sm">
          Tu paquete de {" "}
          <strong>{TYPE_LABELS[shipment.shipment_type] || shipment.shipment_type}</strong>{" "}
          será entregado a <strong>{shipment.recipient_name}</strong> en{" "}
          <strong data-testid="envio-camp">{shipment.recipient_camp}</strong>.
        </p>
      </div>

      <div className="mt-8 rounded-2xl border border-neutral-200 bg-white p-6">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4 flex items-center gap-2">
          <span>Estado del envío</span>
          {shipment.paid ? (
            <span
              data-testid="paid-pill"
              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-medium normal-case tracking-normal"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Pagado
            </span>
          ) : null}
        </div>
        <StatusTimeline
          steps={SHIPMENT_STEPS}
          currentCode={shipment.status}
          cancelledMessage="Este envío ha sido cancelado. Si crees que se trata de un error, contacta con soporte."
        />
      </div>

      <div className="mt-6 grid sm:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="text-xs text-neutral-500 flex items-center gap-1">
            <Package className="h-3 w-3" /> Tipo
          </div>
          <div className="mt-1 font-semibold">
            {TYPE_LABELS[shipment.shipment_type] || shipment.shipment_type}
          </div>
          <div className="text-sm text-neutral-500 mt-0.5" data-testid="envio-size">
            {(() => {
              // Phase 46b — the wizard-v2 fixed-tier flows persist NO weight
              // (nevera / patinete / silla_ruedas), so falling back to
              // `${weight_kg} kg` would render "null kg". Prefer the
              // canonical size_label; fall back to weight only when it's
              // actually populated.
              if (shipment.size_label) return shipment.size_label;
              if (shipment.shipment_type === "tv" && shipment.tv_inches) {
                return `${shipment.tv_inches}"`;
              }
              if (shipment.weight_kg) return `${shipment.weight_kg} kg`;
              return "";
            })()}
          </div>
          {shipment.shipment_type === "electronica" && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {/* Wizard-v2 subcategory badge (takes precedence). */}
              {shipment.shipment_subcategory && (
                <span
                  data-testid="envio-shipment-subcategory"
                  className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-800 px-2.5 py-0.5 text-xs font-semibold border border-emerald-100"
                >
                  {SUBCATEGORY_LABELS[shipment.shipment_subcategory] ||
                    shipment.shipment_subcategory}
                </span>
              )}
              {/* Legacy subtype badge — kept for historical shipments only. */}
              {!shipment.shipment_subcategory && shipment.electronics_subtype && (
                <span
                  data-testid="envio-electronics-subtype"
                  className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 px-2.5 py-0.5 text-xs font-semibold border border-indigo-100"
                >
                  Categoría: {ELECTRONICS_SUBTYPE_LABELS[shipment.electronics_subtype] || shipment.electronics_subtype}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="text-xs text-neutral-500">Estado</div>
          <div className="mt-1">
            <Badge
              data-testid="envio-status-badge"
              className={STATUS_CLASSES[status] || "bg-neutral-200 text-neutral-700"}
            >
              {STATUS_LABELS[status] || status}
            </Badge>
          </div>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="text-xs text-neutral-500">Coste</div>
          <div className="mt-1 font-black text-xl" data-testid="envio-fee">
            {formatEUR(shipment.fee_eur)}
          </div>
        </div>
      </div>

      <section className="mt-8 grid sm:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="text-xs text-neutral-500 mb-2">Remitente</div>
          <div className="font-semibold">{shipment.sender_name}</div>
          {shipment.sender_address && (
            <div className="text-sm text-neutral-600">
              {shipment.sender_address}
            </div>
          )}
          {(shipment.sender_postal_code || shipment.sender_city || shipment.sender_province) && (
            <div className="text-sm text-neutral-600">
              {[shipment.sender_postal_code, shipment.sender_city].filter(Boolean).join(" ")}
              {shipment.sender_province ? ` (${shipment.sender_province})` : ""}
            </div>
          )}
          <div className="text-sm text-neutral-600">{shipment.sender_email}</div>
          <div className="text-sm text-neutral-600">{shipment.sender_phone}</div>
          {shipment.payment_method && (
            <div className="mt-3">
              <span
                data-testid="envio-payment-method"
                className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 text-xs font-semibold border border-emerald-100"
              >
                {shipment.payment_method === "efectivo"
                  ? "Pago: efectivo al recoger"
                  : "Pago: tarjeta online"}
              </span>
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="text-xs text-neutral-500 mb-2 flex items-center gap-1">
            <MapPin className="h-3 w-3" /> Destinatario
          </div>
          <div className="font-semibold">{shipment.recipient_name}</div>
          <div className="text-sm text-neutral-600">{shipment.recipient_phone}</div>
          <div className="text-sm text-neutral-600">
            {shipment.recipient_camp} · {shipment.recipient_daira} ·{" "}
            {shipment.recipient_barrio}
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6">
        <h2 className="text-lg font-black tracking-tight mb-3">Descripción</h2>
        <p className="text-sm text-neutral-700 whitespace-pre-wrap">
          {shipment.description}
        </p>
        <Separator className="my-4" />
        <div className="flex justify-between font-bold">
          <span>Total</span>
          <span>{formatEUR(shipment.fee_eur)}</span>
        </div>
      </section>

      <WhatsAppContextualLink kind="envio" shortId={shortId} testId="envio-whatsapp-link" />

      <div className="mt-6 flex flex-wrap gap-3">
        {user ? (
          <Button
            asChild
            data-testid="envio-to-my-shipments-btn"
            className="rounded-full bg-[#00953B] hover:bg-[#007a30] text-white"
          >
            <Link to="/mis-envios">Ver mis envíos</Link>
          </Button>
        ) : (
          <div
            data-testid="envio-guest-note"
            className="text-sm rounded-2xl bg-amber-50 text-amber-800 border border-amber-100 px-4 py-3"
          >
            Guarda este enlace o{" "}
            <Link to="/registro" className="underline font-semibold">
              crea una cuenta
            </Link>{" "}
            para volver a verlo.
          </div>
        )}
        <Button
          variant="outline"
          onClick={copyLink}
          data-testid="envio-copy-link-btn"
          className="rounded-full border-neutral-200 gap-2"
        >
          <Copy className="h-4 w-4" /> Copiar enlace
        </Button>
        <Button
          onClick={downloadPdf}
          data-testid="envio-download-pdf-btn"
          className="rounded-full bg-[#00953B] hover:bg-[#007a30] text-white gap-2"
        >
          <Download className="h-4 w-4" /> Descargar recibo (PDF)
        </Button>
        <WhatsAppActionButton
          kind="envio"
          shortId={shortId}
          shipmentId={shipment.id}
          testId="envio-open-whatsapp-btn"
        />
        <Button
          asChild
          variant="outline"
          className="rounded-full border-neutral-200"
        >
          <Link to="/enviar-paquete">Nuevo envío</Link>
        </Button>
      </div>
      {shipment.status === "entregado" && (
        <DeliveryEvidenceCard shipmentId={shipment.id} />
      )}
    </div>
  );
}
